/* eslint-disable no-underscore-dangle */
import { PRERENDER_DEPS } from '@web-platform/shared-universal-constants/src/homepage/prerenderDependencies';
import { errorLogging } from '@web-platform/shared-universal-redux/src/middleware/errorLogging';
import { composeEnhancers } from '@web-platform/shared-universal-redux/src/state/composeEnhancers';
import { createDynamicStore } from '@web-platform/shared-universal-redux/src/state/createDynamicStore';
import { setStore } from '@web-platform/shared-universal-redux/src/store';
import {
  isAcornRequest,
  isAcornType,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/isAcornRequest';
import { applyMiddleware } from 'redux';
import { GLOBAL } from 'saddlebag-browser';

import {
  observeFatalError,
  observeQueryChanges,
  observeMutationChanges,
} from './network-observers';
import { observeSessionStorageState } from './sessionStorageObserver';

import type {
  StoreType,
  BananaStore,
  AcornStore,
} from '@web-platform/shared-universal-types/src/redux/state/state-shared-types';

const $window = GLOBAL.getWindow();

const createEnhancer = async () => {
  if (isAcornRequest()) {
    const { loadGoogleAndCrossSellAdverts, logAdvertsReceived } = await import(
      /* webpackChunkName: "Acorn-Middleware-Adverts" */
      '@web-platform/shared-acorn-redux/src/middleware/adverts'
    );
    return composeEnhancers(
      // Must always be first to catch errors in other middleware
      applyMiddleware(errorLogging),
      applyMiddleware(logAdvertsReceived),
      applyMiddleware(loadGoogleAndCrossSellAdverts),
    );
  }

  const { advertsLogging } = await import(
    /* webpackChunkName: "Banana-Middleware-Adverts" */
    '@web-platform/shared-banana-redux/src/middleware/adverts'
  );

  return composeEnhancers(
    // Must always be first to catch errors in other middleware
    applyMiddleware(errorLogging),
    applyMiddleware(advertsLogging),
  );
};

const createStore = async (): Promise<StoreType> => {
  const enhancer = await createEnhancer();

  if (isAcornRequest()) {
    const { staticReducers } = await import(
      /* webpackChunkName: "Acorn-Reducers" */
      '@web-platform/shared-acorn-redux/src/state'
    );
    return createDynamicStore({
      staticReducers,
      initialState: {},
      enhancer,
    });
  }

  const { staticReducers } = await import(
    /* webpackChunkName: "Banana-Reducers" */
    '@web-platform/shared-banana-redux/src/state'
  );
  return createDynamicStore({
    staticReducers,
    initialState: {},
    enhancer,
  });
};

const setupStore = async (): Promise<StoreType> => {
  const store = await createStore();

  // Add store observer once the store is created
  await observeSessionStorageState(store);
  await observeFatalError(store);
  observeQueryChanges(store);
  observeMutationChanges(store);

  if (isAcornType<AcornStore, BananaStore>(store)) {
    const { observeLocalStorageState } = await import(
      /* webpackChunkName: "Acorn-observeLocalStorageState" */
      '@web-platform/shared-acorn-redux/src/helpers/localStorageObserver'
    );
    observeLocalStorageState(store);
  }

  $window.banana = $window.banana || {};
  $window.banana.__DEBUG__ = $window.banana.__DEBUG__ || {};
  $window.banana.__DEBUG__.store = store;

  $window.banana.prerenderDependencies = {
    [PRERENDER_DEPS.SEO]: false,
    [PRERENDER_DEPS.FOOTER]: false,
  };

  setStore(store);

  return store;
};

export { setupStore };
